import { forwardRef, useImperativeHandle } from 'react';
import type { VoiceAnalysis } from '@/api/voice';
import { useVoiceRecording } from '@/hooks/useVoiceRecording';
import { inspectionsApi } from '@/api/inspections';
import { Button, cn } from '@/ui';
import VoiceAnalysisCard from './VoiceAnalysisCard';

export interface VoiceRecordButtonProps {
  reportId?: string;
  reportItemId?: string;
  roomId?: string;
  componentId?: string;
  inspectionId?: string;
  context?: {
    roomOrArea?: string;
    componentName?: string;
    propertyAddress?: string;
  };
  onAnalysisComplete?: (analysis: VoiceAnalysis) => void;
  onDiscard?: () => void;
  /** Hide the built-in floating action button — use when the caller provides its own trigger (e.g. QuickActionBar). */
  hideFab?: boolean;
}

/** Imperative handle exposed to parents that want to drive recording from their own UI. */
export interface VoiceRecordHandle {
  startRecording: () => void;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

const VoiceRecordButton = forwardRef<VoiceRecordHandle, VoiceRecordButtonProps>(function VoiceRecordButton(
  {
    reportId,
    reportItemId,
    roomId,
    componentId,
    inspectionId,
    context,
    onAnalysisComplete,
    onDiscard,
    hideFab = false,
  },
  ref,
) {
  const {
    state: recordingState,
    transcript,
    elapsedSeconds: elapsedTime,
    analysis,
    error,
    startRecording,
    stopRecording,
    retryAnalysis,
    reset,
  } = useVoiceRecording({ reportId, reportItemId, roomId, componentId, inspectionId, context });

  useImperativeHandle(ref, () => ({ startRecording }), [startRecording]);

  // Offline save is indicated by state=done with an error message (no analysis object)
  const savedOffline = recordingState === 'done' && error !== null && analysis === null;

  const handleMicPress = () => {
    if (recordingState === 'idle' || recordingState === 'error') {
      startRecording();
    } else if (recordingState === 'recording') {
      stopRecording();
    }
  };

  const handleDiscard = async () => {
    if (inspectionId && analysis?.findingIds?.length) {
      await Promise.allSettled(
        analysis.findingIds.map((fid) => inspectionsApi.deleteFinding(inspectionId, fid))
      );
    }
    reset();
    onDiscard?.();
  };

  const handleReview = (result: VoiceAnalysis) => {
    onAnalysisComplete?.(result);
    reset();
  };

  const showFab = !hideFab && (recordingState === 'idle' || recordingState === 'error') && !analysis;

  return (
    <>
      {showFab && (
        <button
          type="button"
          onClick={handleMicPress}
          className={cn(
            'fixed bottom-24 md:bottom-6 right-4 md:right-6 w-14 h-14 rounded-full z-40',
            'bg-brand text-ink-inverse shadow-lg hover:brightness-95 hover:shadow-xl',
            'transition-all flex items-center justify-center',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          )}
          title="Record voice findings"
          aria-label="Record voice findings"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
          </svg>
        </button>
      )}

      {recordingState !== 'idle' && (
        <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-50">
          <div className="bg-surface-1 w-full sm:max-w-md sm:rounded-xl rounded-t-xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border-subtle">
              <h3 className="text-heading-md text-ink">
                {recordingState === 'recording' && 'Recording...'}
                {recordingState === 'processing' && 'Analyzing...'}
                {recordingState === 'done' && 'Voice Analysis'}
                {recordingState === 'error' && 'Recording Error'}
              </h3>
              <button
                type="button"
                onClick={handleDiscard}
                className="text-ink-subtle hover:text-ink p-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
                aria-label="Close"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="p-4 space-y-4">
              {recordingState === 'recording' && (
                <>
                  <div className="flex flex-col items-center py-4 space-y-4">
                    <div className="relative flex items-center justify-center">
                      <span className="absolute inline-flex h-20 w-20 rounded-full bg-brand/40 animate-ping" aria-hidden />
                      <button
                        type="button"
                        onClick={handleMicPress}
                        className={cn(
                          'relative w-20 h-20 rounded-full shadow-lg flex items-center justify-center',
                          'bg-brand text-ink-inverse hover:brightness-95 transition-colors',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
                        )}
                        title="Tap to stop"
                        aria-label="Stop recording"
                      >
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                        </svg>
                      </button>
                    </div>
                    <p className="text-body-sm text-ink-subtle">Tap to stop</p>
                    <p className="text-body-lg font-mono font-semibold text-ink tabular-nums">
                      {formatElapsed(elapsedTime)}
                    </p>
                  </div>

                  {transcript ? (
                    <div className="bg-surface-2 rounded-lg p-3 min-h-[64px]">
                      <p className="text-caption text-ink-faint mb-1">Live transcript</p>
                      <p className="text-body-sm text-ink-muted leading-relaxed italic">{transcript}</p>
                    </div>
                  ) : (
                    <div className="bg-surface-2 rounded-lg p-3 min-h-[64px] flex items-center justify-center">
                      <p className="text-body-sm text-ink-faint">Listening for speech...</p>
                    </div>
                  )}
                </>
              )}

              {recordingState === 'processing' && (
                <div className="py-8 text-center space-y-4">
                  <div className="flex items-center justify-center gap-2 text-brand">
                    <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24" aria-hidden>
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span className="text-body-md font-medium">Analyzing findings...</span>
                  </div>
                  <p className="text-caption text-ink-faint">This usually takes a few seconds</p>
                  {transcript && (
                    <div className="bg-surface-2 rounded-lg p-3 text-left">
                      <p className="text-caption text-ink-faint mb-1">Transcript</p>
                      <p className="text-body-sm text-ink-muted italic">{transcript}</p>
                    </div>
                  )}
                </div>
              )}

              {recordingState === 'done' && savedOffline && (
                <div className="space-y-3">
                  <div className="p-4 bg-sev-major-soft border border-sev-major/30 rounded-lg flex items-start gap-3">
                    <svg className="w-5 h-5 text-sev-major shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                    </svg>
                    <div>
                      <p className="text-body-sm font-medium text-sev-major-ink">Saved offline</p>
                      <p className="text-caption text-sev-major-ink/80 mt-0.5">Will sync automatically when you reconnect.</p>
                    </div>
                  </div>
                  {transcript && (
                    <div className="bg-surface-2 rounded-lg p-3">
                      <p className="text-caption text-ink-faint mb-1">Saved transcript</p>
                      <p className="text-body-sm text-ink-muted italic">{transcript}</p>
                    </div>
                  )}
                  <Button variant="secondary" onClick={handleDiscard} className="w-full">
                    Done
                  </Button>
                </div>
              )}

              {recordingState === 'done' && !savedOffline && analysis && (
                <VoiceAnalysisCard
                  analysis={analysis}
                  roomName={context?.roomOrArea}
                  onReview={() => handleReview(analysis)}
                  onDiscard={handleDiscard}
                />
              )}

              {recordingState === 'error' && (
                <div className="space-y-4">
                  <div className="p-4 bg-sev-critical-soft border border-sev-critical/30 rounded-lg flex items-start gap-3">
                    <svg className="w-5 h-5 text-sev-critical shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                    </svg>
                    <div>
                      <p className="text-body-sm font-medium text-sev-critical-ink">Recording failed</p>
                      {error && <p className="text-caption text-sev-critical-ink/80 mt-0.5">{error}</p>}
                    </div>
                  </div>
                  {transcript && (
                    <div className="bg-surface-2 rounded-lg p-3">
                      <p className="text-caption text-ink-faint mb-1">Your transcript (not lost)</p>
                      <p className="text-body-sm text-ink-muted italic">{transcript}</p>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="secondary" onClick={handleDiscard} className="flex-1">
                      Cancel
                    </Button>
                    {transcript ? (
                      <Button onClick={retryAnalysis} className="flex-1">
                        Retry Analysis
                      </Button>
                    ) : (
                      <Button onClick={handleMicPress} className="flex-1">
                        Try Again
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default VoiceRecordButton;
